import React from "react";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import { formatMoney } from "utils/formatters/CurrencyFormatters";
import { formatDateAsLocaleString, formatDateOnly, formatDateShort } from "utils/formatters/DateFormatters";
import { OrderStatus, SalesMotion } from "client/api-v2";
import type { OrderSummaryDto } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { EmptyValue, MutedPlaceholder } from "components/MutedPlaceholder";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { ContactsHoverCard } from "areas/orders/components/chrome/ContactsHoverCard";
import type { ContactPartyView } from "areas/orders/components/chrome/ContactsHoverCard";
import { LicenceGlyph } from "areas/orders/components/chrome/LicenceGlyph";
import { OrderStatusChip } from "areas/orders/components/chrome/OrderStatusChip";
import { ReviewChip } from "areas/orders/components/chrome/ReviewChip";
import { ViaResellerBadge } from "areas/orders/components/chrome/ViaResellerBadge";
import { orderRoutes } from "areas/orders/order-routes";
import { crmAccountUrl, productPlatformLabels } from "areas/orders/orderConstants";
import { describeFulfilmentStatus } from "areas/orders/utils/fulfilmentStatus";
import { isQuoteExpiringSoon, isSubscriptionTermEndingSoon } from "areas/orders/utils/orderWarnings";
import { salesRepDisplayName } from "areas/orders/utils/salesRepDisplayName";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

interface OrderListProps {
  orders: OrderSummaryDto[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize?: number;
  columnVisibility?: GridColumnVisibilityModel;
  onColumnVisibilityChange?: (model: GridColumnVisibilityModel) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export const toggleableColumnFields = {
  fulfilment: "fulfilment",
  term: "termEndDate",
  lastUpdated: "lastModifiedDate",
} as const;

function CellLine({ children, colour, fontSize }: { children: React.ReactNode; colour?: string; fontSize?: string }) {
  return (
    <Typography variant="caption" component="div" sx={{ color: colour ?? "text.secondary", lineHeight: 1.5, fontSize }}>
      {children}
    </Typography>
  );
}

function purchasingParties(order: OrderSummaryDto): { endCustomer: ContactPartyView; reseller?: ContactPartyView } {
  const endCustomer: ContactPartyView = {
    legalName: order.billingAccountLegalName,
    isIndividual: false,
    contacts: [],
    unavailable: true,
  };

  if (!order.isViaReseller || order.resellerBillingAccountLegalName === null) {
    return { endCustomer };
  }

  return {
    endCustomer,
    reseller: {
      legalName: order.resellerBillingAccountLegalName,
      isIndividual: false,
      contacts: [],
      unavailable: true,
    },
  };
}

function CustomerCell({ order }: { order: OrderSummaryDto }) {
  const { endCustomer, reseller } = purchasingParties(order);

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <LicenceGlyph odNumber={order.odNumber} />
        <Typography variant="body2">{order.billingAccountLegalName}</Typography>
        {order.isViaReseller && (
          <ViaResellerBadge resellerLegalName={order.resellerBillingAccountLegalName ?? undefined} />
        )}
      </Stack>
      <CellLine>
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {order.odNumber}
        </Box>
        {" · "}
        <ContactsHoverCard endCustomer={endCustomer} reseller={reseller}>
          <Link component={RouterLink} to={billingAccountRoutes.detail(order.billingAccountCode).root}>
            Billing Account
          </Link>
        </ContactsHoverCard>
        {" · "}
        {order.billingAccountCrmId === null ? (
          <MutedPlaceholder>No CRM account</MutedPlaceholder>
        ) : (
          <Link href={crmAccountUrl(order.billingAccountCrmId)} target="_blank" rel="noopener">
            CRM Account
          </Link>
        )}
      </CellLine>
      <CellLine>
        {"Subscription: "}
        <Link component={RouterLink} to={subscriptionRoutes.detail(order.subscriptionId).root}>
          {order.subscriptionName ?? order.subscriptionShortId}
        </Link>
        {order.subscriptionName !== null && (
          <>
            {" · "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {order.subscriptionShortId}
            </Box>
          </>
        )}
      </CellLine>
      <CellLine>
        {order.subscriptionSerial === null ? (
          <MutedPlaceholder>Licence key: not issued yet</MutedPlaceholder>
        ) : (
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {`Licence key: ${order.subscriptionSerial}`}
          </Box>
        )}
      </CellLine>
    </Box>
  );
}

function PlanCell({ order }: { order: OrderSummaryDto }) {
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2">{productPlatformLabels[order.platform]}</Typography>
      <Stack direction="row" spacing={0.5}>
        <RemoveIcon fontSize="inherit" titleAccess="Current" sx={{ color: "text.secondary" }} />
        <CellLine>{order.currentPlanName}</CellLine>
      </Stack>
      {order.planName !== order.currentPlanName && (
        <Stack direction="row" spacing={0.5}>
          <ArrowForwardIcon fontSize="inherit" aria-hidden sx={{ color: "success.main" }} />
          <CellLine>{order.planName}</CellLine>
        </Stack>
      )}
    </Box>
  );
}

function describeQuoteExpiry(validUntil: string, today: DateTime): { label: string; tooltip: string } {
  const daysLeft = Math.round(getOrParseDateTime(validUntil).startOf("day").diff(today.startOf("day"), "days").days);

  if (daysLeft < 0) {
    return { label: "Overdue", tooltip: `Expired ${Math.abs(daysLeft)} days ago` };
  }

  const whenText = daysLeft === 0 ? "today" : daysLeft === 1 ? "tomorrow" : `in ${daysLeft} days`;
  return { label: `Expires ${formatDateShort(getOrParseDateTime(validUntil))}`, tooltip: `Expires ${whenText}` };
}

function StatusCell({ order }: { order: OrderSummaryDto }) {
  const today = DateTime.utc();
  const isQuote = order.status === OrderStatus.Quote;
  const expiry =
    isQuote && order.validUntil !== null
      ? { ...describeQuoteExpiry(order.validUntil, today), isUrgent: isQuoteExpiringSoon(order, today) }
      : null;

  return (
    <Box sx={{ py: 1 }}>
      <OrderStatusChip status={order.status} sx={{ mb: 0.5 }} />
      {order.isArchived && (
        <Box>
          <Chip label="Archived" size="small" variant="outlined" />
        </Box>
      )}
      {isQuote && order.publishedAt !== null && (
        <CellLine fontSize="0.65rem">
          <Tooltip title={`Published on ${formatDateAsLocaleString(getOrParseDateTime(order.publishedAt))}`}>
            <Box component="span">{`Published ${formatDateShort(getOrParseDateTime(order.publishedAt))}`}</Box>
          </Tooltip>
        </CellLine>
      )}
      {expiry && (
        <CellLine colour={expiry.isUrgent ? "warning.main" : undefined} fontSize="0.65rem">
          <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
            {expiry.isUrgent && <WarningAmberIcon fontSize="inherit" titleAccess="Quote expiring soon" />}
            <Tooltip title={expiry.tooltip}>
              <Box component="span">{expiry.label}</Box>
            </Tooltip>
          </Stack>
        </CellLine>
      )}
    </Box>
  );
}

function coveredUntil(order: OrderSummaryDto): string | null {
  if (order.status === OrderStatus.Accepted) {
    return order.termEndDate;
  }

  return order.salesMotion === SalesMotion.Renewal ? order.termStartDate : null;
}

function TermEndCell({ order }: { order: OrderSummaryDto }) {
  const endDate = coveredUntil(order);
  if (endDate === null) {
    return <EmptyValue />;
  }

  const today = DateTime.utc();
  const daysLeft = Math.round(getOrParseDateTime(endDate).startOf("day").diff(today.startOf("day"), "days").days);

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <span>{formatDateOnly(getOrParseDateTime(endDate))}</span>
      {isSubscriptionTermEndingSoon(order, today) && (
        <Tooltip title={`Expires in ${daysLeft} days`} describeChild>
          <WarningAmberIcon fontSize="small" titleAccess="Term ending soon" sx={{ color: "warning.main" }} />
        </Tooltip>
      )}
    </Stack>
  );
}

function FulfilmentCell({ order }: { order: OrderSummaryDto }) {
  const { headline, detail } = describeFulfilmentStatus(order);

  return (
    <Tooltip title={detail ?? ""}>
      <Box component="span">{headline}</Box>
    </Tooltip>
  );
}

function InstantCell({ instant, tooltip }: { instant: string; tooltip: string }) {
  return (
    <Tooltip title={`${tooltip} ${formatDateAsLocaleString(getOrParseDateTime(instant))}`}>
      <span>{formatDateOnly(getOrParseDateTime(instant))}</span>
    </Tooltip>
  );
}

function OpenOrderButton({ order }: { order: OrderSummaryDto }) {
  const label = "View";

  return (
    <Button
      size="small"
      variant="outlined"
      component={RouterLink}
      to={orderRoutes.detail(order.odNumber).root}
      aria-label={`${label} ${order.odNumber}`}
    >
      {label}
    </Button>
  );
}

// Why: the list endpoint takes no sort parameter, so a sortable column would reorder only the
// page on screen while looking as though it reordered every result.
const columnDefinitions: GridColDef<OrderSummaryDto>[] = [
  {
    field: "actions",
    headerName: "Actions",
    minWidth: 100,
    align: "center",
    sortable: false,
    renderCell: ({ row }) => <OpenOrderButton order={row} />,
  },
  {
    field: "billingAccountLegalName",
    headerName: "Customer",
    minWidth: 300,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => <CustomerCell order={row} />,
  },
  {
    field: "salesRep",
    headerName: "Sales Rep",
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) =>
      row.salesRep === null ? (
        <MutedPlaceholder>Self-serve</MutedPlaceholder>
      ) : (
        <Tooltip title={row.salesRepEmail ?? ""}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {salesRepDisplayName(row.salesRep)}
          </Box>
        </Tooltip>
      ),
  },
  {
    field: "salesMotion",
    headerName: "Motion",
    minWidth: 100,
    sortable: false,
  },
  {
    field: "planName",
    headerName: "Plan",
    minWidth: 170,
    sortable: false,
    renderCell: ({ row }) => <PlanCell order={row} />,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <StatusCell order={row} />,
  },
  {
    field: toggleableColumnFields.fulfilment,
    headerName: "Fulfilment",
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <FulfilmentCell order={row} />,
  },
  {
    field: "reviewRequired",
    headerName: "Review",
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => (row.reviewRequired ? <ReviewChip /> : null),
  },
  {
    field: toggleableColumnFields.term,
    headerName: "Term End",
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <TermEndCell order={row} />,
  },
  {
    field: "total",
    headerName: "Value",
    minWidth: 110,
    align: "right",
    headerAlign: "right",
    sortable: false,
    renderCell: ({ row }) => formatMoney(row.total, row.currency),
  },
  {
    field: "createdDate",
    headerName: "Created",
    minWidth: 110,
    align: "right",
    headerAlign: "right",
    sortable: false,
    renderCell: ({ row }) => <InstantCell instant={row.createdDate} tooltip={`Created by ${row.createdByUser} on`} />,
  },
  {
    field: toggleableColumnFields.lastUpdated,
    headerName: "Last updated",
    minWidth: 130,
    align: "right",
    headerAlign: "right",
    sortable: false,
    renderCell: ({ row }) => (
      <InstantCell instant={row.lastModifiedDate} tooltip={`Last updated by ${row.lastModifiedByUser} on`} />
    ),
  },
];

const columns: GridColDef<OrderSummaryDto>[] = columnDefinitions.map((column) => ({
  ...column,
  display: "flex",
}));

export function OrderList({
  orders,
  totalCount,
  loading,
  page,
  pageSize,
  columnVisibility,
  onColumnVisibilityChange,
  onPageChange,
  onPageSizeChange,
}: OrderListProps) {
  return (
    <DataTable
      rows={orders}
      columns={columns}
      rowCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      pageSizeOptions={[10, 15, 25, 50]}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={onColumnVisibilityChange}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      getRowHeight={() => "auto"}
      pagingMode="server"
      getRowId={(row) => row.odNumber}
    />
  );
}
